import {liveSessionRef} from '../live/observability.js';

export const DYMEK_ANALYTICS_EVENTS=Object.freeze([
  'dymek_opened',
  'prompt_submitted',
  'response_completed',
  'response_failed',
  'dymek_feedback_submitted',
  'dymek_reference_opened'
]);

const ALLOWED=new Set(DYMEK_ANALYTICS_EVENTS);
const SURFACES=new Set([
  'home','recipe','planner','active_session','completed_session','session'
]);
const LINK_KINDS=new Set(['session','stage','recipe','planner']);

function choice(value,allowed,fallback='unknown'){
  const key=String(value??'').trim().toLowerCase();
  return allowed.has(key)?key:fallback;
}
function count(value,max=1000){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}
function latency(value){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(600_000,Math.floor(number)));
}
function safeRunId(value){
  const id=String(value??'').trim();
  return /^[a-zA-Z0-9_-]{8,80}$/.test(id)?id:null;
}
function safeError(value){
  const code=String(value??'').trim().toUpperCase();
  return /^[A-Z][A-Z0-9_.:-]{0,79}$/.test(code)?code:'UNCLASSIFIED_ERROR';
}
function surface(value){return choice(value,SURFACES,'home');}
function sessionRef(value){return value?liveSessionRef(value):null;}

function correlation(input={}){
  const session=sessionRef(input.sessionId);
  const source=sessionRef(input.sourceSessionId);
  const planner=safeRunId(input.plannerRunId);
  return Object.freeze({
    ...(session?{session_ref:session}:{}),
    ...(source?{source_session_ref:source}:{}),
    ...(planner?{planner_run_id:planner}:{})
  });
}

function evidenceCounts(response={}){
  const claims=Array.isArray(response?.claims)?response.claims:[];
  const counts={
    observed:0,
    validated_knowledge:0,
    derived_calculation:0,
    general_suggestion:0
  };
  for(const claim of claims){
    if(Object.hasOwn(counts,claim?.evidenceClass))counts[claim.evidenceClass]+=1;
  }
  const summary=response?.evidenceSummary?.evidenceClassCounts||{};
  for(const key of Object.keys(counts)){
    if(Number.isFinite(Number(summary[key])))counts[key]=count(summary[key],100);
  }
  return Object.freeze({
    observed_count:count(counts.observed,100),
    validated_knowledge_count:count(counts.validated_knowledge,100),
    derived_calculation_count:count(counts.derived_calculation,100),
    general_suggestion_count:count(counts.general_suggestion,100),
    safety_fallback:Boolean(response?.fallback||response?.evidenceSummary?.safetyFallback),
    validated_knowledge_used:counts.validated_knowledge>0
  });
}

function properties(name,input={}){
  const common=Object.freeze({
    context_surface:surface(input.contextSurface),
    interaction_id:safeRunId(input.interactionId)||'missing',
    ...correlation(input)
  });

  switch(name){
    case 'dymek_opened':
      return Object.freeze({
        context_surface:surface(input.contextSurface),
        ...correlation(input)
      });
    case 'prompt_submitted':
      return Object.freeze({
        ...common,
        private_text_included:Boolean(input.privateTextIncluded),
        provider_consent:Boolean(input.providerConsent),
        retry:Boolean(input.retry)
      });
    case 'response_completed':
      return Object.freeze({
        ...common,
        ...evidenceCounts(input.response),
        latency_ms:latency(input.latencyMs),
        provider_used:Boolean(input.response?.providerUsed),
        provider_consent_required:Boolean(input.response?.providerConsentRequired),
        history_selection_required:input.response?.status==='needs_history_selection',
        claim_count:count(input.response?.claims?.length,100),
        link_count:count(input.response?.links?.length,50)
      });
    case 'response_failed':
      return Object.freeze({
        ...common,
        latency_ms:latency(input.latencyMs),
        error_code:safeError(input.errorCode)
      });
    case 'dymek_feedback_submitted':
      return Object.freeze({
        ...common,
        helpful:Boolean(input.helpful)
      });
    case 'dymek_reference_opened':
      return Object.freeze({
        ...common,
        reference_kind:choice(input.referenceKind,LINK_KINDS,'unknown')
      });
    default:
      throw new TypeError('Unsupported Dymek analytics event');
  }
}

export function createDymekAnalytics({analytics=null}={}){
  function track(name,input={}){
    if(!ALLOWED.has(name))throw new TypeError('Unsupported Dymek analytics event');
    const safe=properties(name,input);
    try{
      return analytics?.track?.(name,safe)
        ??Object.freeze({accepted:false,reason:'ANALYTICS_UNAVAILABLE'});
    }catch{
      return Object.freeze({accepted:false,reason:'ANALYTICS_FAILED'});
    }
  }

  return Object.freeze({
    events:DYMEK_ANALYTICS_EVENTS,
    track,
    opened:input=>track('dymek_opened',input),
    promptSubmitted:input=>track('prompt_submitted',input),
    responseCompleted:input=>track('response_completed',input),
    responseFailed:input=>track('response_failed',input),
    feedbackSubmitted:input=>track('dymek_feedback_submitted',input),
    referenceOpened:input=>track('dymek_reference_opened',input)
  });
}
